import React, { useEffect, useState, useMemo } from 'react';
import Icon from '@mdi/react';
import { mdiDownloadOutline } from '@mdi/js';
import {
    LoadingMeter,
    ProviderPatientStatement,
    ProviderProfessionalClaim,
    ProviderClaim,
    AiAssistant,
    ExtensionUnavailableModal,
    useTalkToAiTrigger,
} from '@enter/core';
import { Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as S from './statement-details.styles';
import {
    IStatementDetailsProps,
    StatementTabId,
} from './statement-details.types';
import { computeBalanceBreakdownFromStatement } from './balance-breakdown';
import { buildStatementClaimCards } from './components/statement-claim-cards.utils';
import { StatementClaimCards } from './components/statement-claim-cards';
import { StatementCodeKey } from './components/statement-code-key';
import {
    buildStatementCodeKeyItems,
    collectAdjustmentsFromClaimPayments,
} from './components/statement-code-key.utils';
import { StatementPrintPreview } from './print/statement-print-preview';
import type { StatementPrintViewModel } from './print/statement-print.types';
import { StatementDataTable } from './components/statement-data-table';
import {
    buildClaimsById,
    buildClaimsTableRows,
    buildPaymentsData,
    buildPaymentsTableRows,
    buildServicesRows,
    buildStatementLedgerRows,
    computeFilteredBalance,
    filterPaymentsDataByDos,
    filterStatementDataByDos,
} from './statement-details.data';
import { createClaimsTableColumns } from './components/statement-claims-columns';
import { createPaymentsTableColumns } from './components/statement-payments-columns';
import { createServicesTableColumns } from './components/statement-services-columns';
import { useStatementActions } from './use-statement-actions';
import { StatementHeader } from './components/statement-header';
import { StatementBalanceHero } from './components/statement-balance-hero';
import { StatementPatientProviderCards } from './components/statement-patient-provider-cards';
import { StatementTabsBar } from './components/statement-tabs-bar';
import { isInRange } from './utils/statement-filter.utils';
import { formatMoney } from './utils/statement-format.utils';
import { formatDate } from '../../services/primitives/date-utils';
import { buildCsvString, downloadCsvBlob } from '../data-table/components/table-export';

export const StatementDetailsPage: React.FC<IStatementDetailsProps> = props => {
    const [patientStatement, setPatientStatement] = useState<ProviderPatientStatement | undefined>(undefined);
    const [professionals, setProfessionals] = useState<ProviderProfessionalClaim[]>([]);
    const [claimPayments, setClaimPayments] = useState<
        Array<{
            adjustments?: Array<{ group?: string; reasonCode?: string }>;
            items?: Array<{ adjustments?: Array<{ group?: string; reasonCode?: string }> }>;
        }>
    >([]);
    const [claims, setClaims] = useState<ProviderClaim[] | undefined>(undefined);
    const [, setLoading] = useState<boolean>(false);
    const [printPreviewModel, setPrintPreviewModel] = useState<StatementPrintViewModel | null>(null);
    const [dosFilterFrom, setDosFilterFrom] = useState<Date | null>(null);
    const [dosFilterTo, setDosFilterTo] = useState<Date | null>(null);
    const [dosFilterKey, setDosFilterKey] = useState<number>(0);
    const [activeTab, setActiveTab] = useState<StatementTabId>('statement');
    const [statementSearchQuery, setStatementSearchQuery] = useState('');
    const [aiOpen, setAiOpen] = useState(false);
    const navigate = useNavigate();

    const {
        patientId,
        patientStore,
        claimStore,
        dialogContext,
        toastContext,
        PageLayout,
        equalsFilter,
        fetchProfessionalClaims,
        fetchClaimPayments,
        fetchClaimsByPatientId,
        getClaimRowHref,
        showActions = false,
    } = props;

    const paymentsData = useMemo(() => buildPaymentsData(patientStatement), [patientStatement]);

    const statementData = useMemo(
        () => buildStatementLedgerRows(patientStatement, professionals),
        [patientStatement, professionals],
    );

    const filteredStatementData = useMemo(
        () => filterStatementDataByDos(statementData, dosFilterFrom, dosFilterTo),
        [statementData, dosFilterFrom, dosFilterTo],
    );

    const filteredPaymentsData = useMemo(
        () => filterPaymentsDataByDos(paymentsData, patientStatement, dosFilterFrom, dosFilterTo),
        [paymentsData, dosFilterFrom, dosFilterTo, patientStatement],
    );

    const claimsById = useMemo(() => buildClaimsById(claims), [claims]);

    const statementClaimCards = useMemo(
        () =>
            buildStatementClaimCards({
                claims: patientStatement?.claims || [],
                professionals,
                providerClaimsById: claimsById,
                dosFilterFrom,
                dosFilterTo,
                searchQuery: statementSearchQuery,
                getClaimRowHref,
            }),
        [
            patientStatement,
            professionals,
            claimsById,
            dosFilterFrom,
            dosFilterTo,
            statementSearchQuery,
            getClaimRowHref,
        ],
    );

    const claimsTableRows = useMemo(
        () =>
            buildClaimsTableRows({
                statement: patientStatement,
                claimsById,
                dosFilterFrom,
                dosFilterTo,
                getClaimRowHref,
            }),
        [patientStatement, dosFilterFrom, dosFilterTo, claimsById, getClaimRowHref],
    );

    const filteredBalance = useMemo(
        () => computeFilteredBalance(patientStatement, filteredStatementData, dosFilterFrom, dosFilterTo),
        [filteredStatementData, dosFilterFrom, dosFilterTo, patientStatement],
    );

    const balanceBreakdown = useMemo(() => {
        try {
            return computeBalanceBreakdownFromStatement(patientStatement, filteredBalance, claimsById);
        } catch (err) {
            console.warn('Failed to compute balance breakdown', err);
            return computeBalanceBreakdownFromStatement(undefined, filteredBalance, claimsById);
        }
    }, [patientStatement, filteredBalance, claimsById]);

    const servicesRows = useMemo(
        () =>
            buildServicesRows({
                statement: patientStatement,
                professionals,
                dosFilterFrom,
                dosFilterTo,
            }),
        [patientStatement, dosFilterFrom, dosFilterTo, professionals],
    );

    const paymentsTableRows = useMemo(
        () => buildPaymentsTableRows(filteredPaymentsData, patientStatement),
        [filteredPaymentsData, patientStatement],
    );

    const statementCodeKeyItems = useMemo(
        () => buildStatementCodeKeyItems(collectAdjustmentsFromClaimPayments(claimPayments)),
        [claimPayments],
    );

    const {
        handleEditPayment,
        handleAllocatePayment,
        handleRecordPayment,
        handleRecordDiscount,
        handleRecordRefund,
        handleCopyClaimUrl,
        handlePrintPDF,
    } = useStatementActions({
        patientId,
        patientStatement,
        setPatientStatement,
        setLoading,
        setPrintPreviewModel,
        patientStore,
        claimStore,
        dialogContext,
        toastContext,
        equalsFilter,
        filteredStatementData,
        filteredBalance,
        balanceBreakdown,
        statementCodeKeyItems,
        providerClaimsById: claimsById,
    });

    const claimsTableColumns = useMemo(
        () =>
            createClaimsTableColumns({
                onGoToClaim: row => {
                    if (row.href) navigate(row.href);
                },
            }),
        [navigate],
    );
    const servicesTableColumns = useMemo(() => createServicesTableColumns(), []);
    const paymentsTableColumns = useMemo(
        () =>
            createPaymentsTableColumns({
                showActions,
                onEditPayment: handleEditPayment,
                onAllocatePayment: handleAllocatePayment,
            }),
        [showActions, handleEditPayment, handleAllocatePayment],
    );

    const statementTabs = useMemo(
        () => [
            { id: 'statement' as const, label: 'Statements', count: statementClaimCards.length },
            { id: 'claims' as const, label: 'Claims', count: claimsTableRows.length },
            { id: 'payments' as const, label: 'Payments', count: paymentsTableRows.length },
            { id: 'services' as const, label: 'Services', count: servicesRows.length },
        ],
        [statementClaimCards.length, claimsTableRows.length, paymentsTableRows.length, servicesRows.length],
    );

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchData = async (): Promise<void> => {
            if (!patientStatement) {
                try {
                    setLoading(true);
                    const statement = await patientStore.loadPatientStatement(patientId);
                    
                    setPatientStatement(statement as ProviderPatientStatement);
                } catch (err) {
                    dialogContext.showError(err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchData();
        // Intentionally keyed on patientId only (initial load / navigation).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    useEffect(() => {
        const fetchProfessionals = async () => {
            if (patientStatement?.claims?.length) {
                try {
                    const claimIds = patientStatement.claims.map(claim => claim.id);
                    const professionalClaims = await fetchProfessionalClaims(claimIds);
                    setProfessionals(professionalClaims || []);
                } catch (err) {
                    console.error('Error fetching professional claims:', err);
                    setProfessionals([]);
                }
            } else {
                setProfessionals([]);
            }
        };
        fetchProfessionals();
    }, [patientStatement, fetchProfessionalClaims]);

    useEffect(() => {
        const fetchPaymentsForCodeKey = async () => {
            if (!patientStatement?.claims?.length || !fetchClaimPayments) {
                setClaimPayments([]);
                return;
            }

            const claimIds = patientStatement.claims
                .filter(claim => isInRange(claim.fromDos, dosFilterFrom, dosFilterTo))
                .map(claim => claim.id)
                .filter(Boolean) as string[];

            if (claimIds.length === 0) {
                setClaimPayments([]);
                return;
            }

            try {
                const payments = await fetchClaimPayments(claimIds);
                setClaimPayments(payments || []);
            } catch (err) {
                console.error('Error fetching claim payments for code key:', err);
                setClaimPayments([]);
            }
        };
        fetchPaymentsForCodeKey();
    }, [patientStatement, fetchClaimPayments, dosFilterFrom, dosFilterTo]);

    useEffect(() => {
        let isMounted = true;

        const fetchClaims = async () => {
            if (patientId && fetchClaimsByPatientId) {
                try {
                    const patientClaims = await fetchClaimsByPatientId(patientId);
                    
                    if (isMounted) {
                        setClaims(patientClaims || []);
                    }
                } catch (err) {
                    console.error('Error fetching claims:', err);
                    if (isMounted) {
                        setClaims([]);
                    }
                }
            }
        };
        fetchClaims();

        return () => {
            isMounted = false;
        };
    }, [patientId, fetchClaimsByPatientId]);

    const {
        handleClick: handleTalkToAiClick,
        checking: isAiChecking,
        tooltipTitle: aiTooltipTitle,
        showExtensionInstructions,
        closeExtensionInstructions,
        openExtensionStore,
        retryConnection,
    } = useTalkToAiTrigger(() => setAiOpen(true));

    const patient = patientStatement?.patient;
    const patientName =
        patient?.first || patient?.last
            ? [patient?.first, patient?.last].filter(Boolean).join(' ')
            : undefined;

    const downloadCsv = (tab: Extract<StatementTabId, 'claims' | 'payments' | 'services'>): void => {
        const timestamp = new Date().toISOString().slice(0, 10);
        const baseName = patientName ? patientName.toLowerCase().replace(/\s+/g, '-') : 'patient-statement';
        const filename = `${baseName}-${tab}-${timestamp}.csv`;

        if (tab === 'claims') {
            const headers = [
                'Claim',
                'Status',
                'Payer',
                'Charge',
                'Insurance',
                'Adjustment',
                'Patient resp.',
                'Max PR',
                'DOS',
            ];
            const rows = claimsTableRows.map(row => ({
                Claim: row.num,
                Status: row.statusLabel,
                Payer: row.payer,
                Charge: formatMoney(row.charge),
                Insurance: formatMoney(row.insurance),
                Adjustment: formatMoney(row.adjustment, { negative: true }),
                'Patient resp.': formatMoney(row.patientResp),
                'Max PR': row.maxPatientResp === undefined ? '—' : formatMoney(row.maxPatientResp),
                DOS: row.dos ? formatDate(row.dos, 'MMM dd, yyyy') : '—',
            }));
            downloadCsvBlob(buildCsvString(rows, headers), filename);
            return;
        }

        if (tab === 'payments') {
            const headers = ['Date', 'Type', 'Reference', 'Applied to', 'Amount', 'Allocated', 'Unallocated'];
            const rows = paymentsTableRows.map(row => ({
                Date: row.date ? formatDate(row.date, 'MMM dd, yyyy') : '—',
                Type: row.type,
                Reference: row.ref || '',
                'Applied to': row.applied,
                Amount: formatMoney(row.amount),
                Allocated: formatMoney(row.allocatedAmount),
                Unallocated: formatMoney(row.unallocatedAmount),
            }));
            downloadCsvBlob(buildCsvString(rows, headers), filename);
            return;
        }

        const headers = ['CPT', 'Service', 'Claim', 'Diagnosis codes', 'Date', 'Charge'];
        const rows = servicesRows.map(row => ({
            CPT: row.procedure || '—',
            Service: row.description || '—',
            Claim: row.claimId || '—',
            'Diagnosis codes': row.diagnosisCodes.join(', '),
            Date: row.date ? formatDate(row.date, 'MMM dd, yyyy') : '—',
            Charge: row.charge ? formatMoney(Number(row.charge)) : '—',
        }));
        downloadCsvBlob(buildCsvString(rows, headers), filename);
    };

    const renderLoader = (): React.ReactElement => (
        <Fade in={true}>
            <S.Loader>
                <LoadingMeter />
            </S.Loader>
        </Fade>
    );

    const renderPage = (statement: ProviderPatientStatement): React.ReactElement => (
        <PageLayout>
            <StatementHeader
                patientName={patientName}
                externalId={patient?.externalId}
                showActions={showActions}
                onCopyUrl={handleCopyClaimUrl}
                onPrint={handlePrintPDF}
                onRecordDiscount={handleRecordDiscount}
                onRecordRefund={handleRecordRefund}
                onRecordPayment={handleRecordPayment}
                aiTooltipTitle={aiTooltipTitle}
                isAiChecking={isAiChecking}
                onTalkToAiClick={handleTalkToAiClick}
            />

            <StatementBalanceHero balanceBreakdown={balanceBreakdown} />

            <StatementPatientProviderCards
                patient={statement.patient}
                billingProvider={statement.billingProvider}
            />

            <StatementTabsBar tabs={statementTabs} activeTab={activeTab} onTabChange={setActiveTab} />

            {activeTab === 'statement' && (
                <S.TabPanel role="tabpanel">
                    <StatementClaimCards
                        cards={statementClaimCards}
                        searchQuery={statementSearchQuery}
                        onSearchQueryChange={setStatementSearchQuery}
                        dosFilterFrom={dosFilterFrom}
                        dosFilterTo={dosFilterTo}
                        dosFilterKey={dosFilterKey}
                        onDosFilterFromChange={setDosFilterFrom}
                        onDosFilterToChange={setDosFilterTo}
                        onClearFilters={() => {
                            setStatementSearchQuery('');
                            setDosFilterFrom(null);
                            setDosFilterTo(null);
                            setDosFilterKey(key => key + 1);
                        }}
                    />
                    <StatementCodeKey items={statementCodeKeyItems} />
                </S.TabPanel>
            )}

            {activeTab === 'claims' && (
                <S.TabPanel role="tabpanel">
                    <S.TabActionsRow>
                        <S.HeaderOutlinedButton type="button" variant="unstyled" onClick={() => downloadCsv('claims')}>
                            <Icon path={mdiDownloadOutline} size={0.75} />
                            Download CSV
                        </S.HeaderOutlinedButton>
                    </S.TabActionsRow>
                    <StatementDataTable
                        name="patient-statement-claims"
                        columns={claimsTableColumns}
                        rows={claimsTableRows}
                        emptyMessage="No claims found"
                        onRowClick={
                            getClaimRowHref
                                ? row => {
                                      if (row.href) navigate(row.href);
                                  }
                                : undefined
                        }
                        isRowClickable={row => !!row.href}
                    />
                </S.TabPanel>
            )}

            {activeTab === 'payments' && (
                <S.TabPanel role="tabpanel">
                    <S.TabActionsRow>
                        <S.HeaderOutlinedButton
                            type="button"
                            variant="unstyled"
                            onClick={() => downloadCsv('payments')}>
                            <Icon path={mdiDownloadOutline} size={0.75} />
                            Download CSV
                        </S.HeaderOutlinedButton>
                    </S.TabActionsRow>
                    <StatementDataTable
                        name="patient-statement-payments"
                        columns={paymentsTableColumns}
                        rows={paymentsTableRows}
                        emptyMessage="No payments recorded yet."
                    />
                </S.TabPanel>
            )}

            {activeTab === 'services' && (
                <S.TabPanel role="tabpanel">
                    <S.TabActionsRow>
                        <S.HeaderOutlinedButton type="button" variant="unstyled" onClick={() => downloadCsv('services')}>
                            <Icon path={mdiDownloadOutline} size={0.75} />
                            Download CSV
                        </S.HeaderOutlinedButton>
                    </S.TabActionsRow>
                    <StatementDataTable
                        name="patient-statement-services"
                        columns={servicesTableColumns}
                        rows={servicesRows}
                        emptyMessage="No services found"
                    />
                </S.TabPanel>
            )}
        </PageLayout>
    );

    return (
        <>
            {patientStatement ? renderPage(patientStatement) : renderLoader()}
            {patientStatement && (
                <>
                    <AiAssistant
                        open={aiOpen}
                        onClose={() => setAiOpen(false)}
                        conversationId={`patientStatement:${patientStatement.patient?.id ?? 'unknown'}`}
                        entity="ProviderPatientStatement"
                        subjectLabel="patient statement"
                        suggestionsKey="default"
                        contextData={patientStatement}
                        utilityKey={`patientStatement:${props.aiUtilitySide ?? 'Provider'}`}
                    />
                    <ExtensionUnavailableModal
                        open={showExtensionInstructions}
                        onClose={closeExtensionInstructions}
                        onGoToStore={openExtensionStore}
                        onRetry={retryConnection}
                    />
                </>
            )}
            {printPreviewModel && (
                <StatementPrintPreview
                    model={printPreviewModel}
                    onClose={() => setPrintPreviewModel(null)}
                    onError={message => toastContext.notify?.(message, 'left', 'warning')}
                />
            )}
        </>
    );
};
